import { unzip, strFromU8 } from 'fflate'

export type BcfVersion = '2.0' | '2.1' | '3.0' | 'unknown'

export interface BcfComment {
  guid: string
  date: string | null
  author: string | null
  comment: string | null
  modifiedDate: string | null
  modifiedAuthor: string | null
  status: string | null
}

export interface BcfTopic {
  guid: string
  index: number | null
  title: string
  type: string | null
  status: string | null
  priority: string | null
  assignedTo: string | null
  description: string | null
  creationAuthor: string | null
  creationDate: string | null
  modifiedAuthor: string | null
  modifiedDate: string | null
  dueDate: string | null
  /** BCF spec-defined Stage value: where in the project lifecycle the
   * topic belongs (e.g. "Programa Base", "Projecto de Execução").
   * Free string, but typically picked from `extensions.stages`. */
  stage: string | null
  labels: string[]
  comments: BcfComment[]
  /** Number of viewpoints declared by the original .bcfzip's markup
   * (informational; the actual files were skipped on import). Used for
   * the read-only banner in the detail panel. */
  viewpointCount: number
  /** IDs into `viewpointsStore` (IndexedDB) for viewpoints attached
   * locally, captured in this browser and to be embedded on export.
   * Empty for topics imported from `.bcfzip` (we don't round-trip the
   * original viewpoints; the user re-attaches if needed). */
  viewpointRefs: string[]
  /** People watching this topic. Free-form list of names / emails;
   * not validated against the workspace user table since the BCF is
   * client-side. Round-tripped through `.bcfzip` via the sidecar
   * `vn2r-topics.json` (BCF spec has no watchers concept) and saved
   * verbatim in the .vn2r-session zip. */
  watchers: string[]
  /** GUIDs de comentários apagados neste tópico (lápides). Só servem à
   * sincronização: sem elas, um comentário apagado cá voltava na união
   * com o que está no servidor. Não vão para o `.bcfzip`. */
  commentsApagados?: string[]
  /** GUIDs dos tópicos relacionados (`RelatedTopic` do BCF). Na 2.1 são
   * `<RelatedTopic Guid="..."/>` soltos no `<Topic>`; na 3.0 vão num
   * contentor `<RelatedTopics>`. A exportação dos conflitos usa-os para
   * ligar os tópicos do mesmo grupo, e a lista agrupa por eles quando
   * não há etiqueta de grupo. Opcional: os tópicos criados antes do
   * campo não o têm, e lê-se como vazio. */
  relatedTopics?: string[]
  /** O tópico como veio do ficheiro importado: os campos editáveis e os
   *  conjuntos, congelados no momento da importação. É a base comum da
   *  primeira sincronização de quem ainda não tem base: dois lados que
   *  importaram o mesmo ficheiro estavam de acordo nisto (ver
   *  `bcfOrigem.ts`). Viaja com o tópico para o servidor, para o outro
   *  lado poder confirmar que a origem é a mesma. Não vai para o
   *  `.bcfzip`. */
  origem?: OrigemDoTopico
}

/** Os valores de um tópico no ficheiro de onde foi importado. */
export interface OrigemDoTopico {
  campos: Record<string, string | null>
  labels: string[]
  watchers: string[]
  viewpointRefs: string[]
  relatedTopics: string[]
}

/** Provenance of a catalog entry, drives UX hints in the dropdown
 * so the user can tell apart values that came from the imported BCF
 * (which the destination tool may or may not recognise) from values
 * the user / VN2R defaults guarantee.
 *  - `default`: from VN2R `defaultExtensions()` (Open / In Progress /
 *    arch disciplines / LNEC stages). Always safe.
 *  - `xml`: declared in the imported BCF's `extensions.xml`. Should
 *    round-trip, but only the importing/exporting tools that know
 *    how to write extensions will preserve it.
 *  - `topic`: auto-seeded from a topic field (no `extensions.xml`
 *    declared it, but a topic used it). On export we WILL declare
 *    it; in the original BCF it was implicit.
 *  - `manual`: the user added the entry via the catalogs panel.
 *    Treated like default for UX. */
export type BcfCatalogSource = 'default' | 'xml' | 'topic' | 'manual'

/** Catalog entry for one of the BCF extension lists (statuses,
 * priorities, types, labels, stages). The label IS the BCF spec
 * value; id is a stable client-side UUID so renames don't break
 * topic references (topics carry the label string verbatim and are
 * matched by case-insensitive equality on render). */
export interface BcfCatalogEntry {
  id: string
  label: string
  /** Hex `#rrggbb` for the badge in the UI. Optional. */
  color: string | null
  /** Lower sorts first in dropdowns / list views. */
  sortOrder: number
  /** Where this entry came from. Older entries may not have it; the
   * UI treats missing as `manual`. */
  source?: BcfCatalogSource
}

/** Status catalog entry adds a flag for terminal states ("Closed",
 * "Resolved", the topic is done). Useful for filters and reporting. */
export interface BcfStatusEntry extends BcfCatalogEntry {
  isTerminal: boolean
}

/** Per-BCF catalogs that the BCF spec calls "extensions". Persisted
 * in the standard `extensions.xml` (round-trips with any BCF tool)
 * plus a sidecar `vn2r-extensions.json` for VN2R-specific metadata
 * (colour, sort order, terminal flag) that the spec doesn't model.
 * Tools that don't preserve unknown files lose the metadata but keep
 * the catalog values. */
export interface BcfExtensions {
  statuses: BcfStatusEntry[]
  priorities: BcfCatalogEntry[]
  types: BcfCatalogEntry[]
  labels: BcfCatalogEntry[]
  stages: BcfCatalogEntry[]
}

export interface ParsedBcf {
  version: BcfVersion
  /** Project GUID extracted from `project.bcfp` (BCF 2.x) or
   * `project.bcfp` ProjectInfo (BCF 3.0). Used to auto-match new
   * imports as additional versions of an existing BCF. Null when
   * the source `.bcfzip` does not declare one. */
  sourceProjectId: string | null
  /** Project name from the same source. Used as a fallback display
   * label when the user hasn't renamed the BCF. */
  sourceProjectName: string | null
  topics: BcfTopic[]
  /** Per-BCF catalogs. Always present (defaultsExtensions() seeds
   * them) so consumers don't have to handle null. */
  extensions: BcfExtensions
  warnings: string[]
}

/** Default catalog seeded for fresh BCFs and used as fallback when an
 * imported `.bcfzip` has no `extensions.xml`. Kept short and BCF-
 * standard so the user sees familiar values on first open. */
export function defaultExtensions(): BcfExtensions {
  const make = (label: string, color: string, sort: number): BcfCatalogEntry => ({
    id: crypto.randomUUID(),
    label,
    color,
    sortOrder: sort,
    source: 'default',
  })
  return {
    statuses: [
      // Labels match BCF spec values emitted by BIMcollab, Solibri,
      // Revit so a fresh `.bcfzip` round-trips without producing
      // overflow extras when re-imported into those tools.
      { ...make('Open', '#4ade80', 0), isTerminal: false },
      { ...make('In Progress', '#fbbf24', 1), isTerminal: false },
      { ...make('Resolved', '#60a5fa', 2), isTerminal: true },
      { ...make('Closed', '#71717a', 3), isTerminal: true },
    ],
    priorities: [
      make('Low', '#94a3b8', 0),
      make('Normal', '#60a5fa', 1),
      make('High', '#fb923c', 2),
      make('Critical', '#ef4444', 3),
    ],
    types: [
      make('Issue', '#fb7185', 0),
      make('Clash', '#f472b6', 1),
      make('Information', '#60a5fa', 2),
      make('Comment', '#a3e635', 3),
    ],
    // Engineering disciplines following common Portuguese AEC practice
    // (LNEC / Ordem dos Engenheiros). Covers most projects; user adds
    // or renames in the catalogs panel as needed.
    labels: [
      make('Arquitectura', '#a8a29e', 0),
      make('Estrutura', '#a16207', 1),
      make('Hidráulica', '#0ea5e9', 2),
      make('AVAC', '#22d3ee', 3),
      make('Elétricas', '#fbbf24', 4),
      make('ITED/ITUR', '#60a5fa', 5),
      make('Gás', '#fb7185', 6),
      make('Elevadores', '#a78bfa', 7),
      make('SCIE', '#ef4444', 8),
      make('Acústica', '#4ade80', 9),
      make('Térmica', '#fb923c', 10),
      make('Acessibilidades', '#c084fc', 11),
      make('Coordenação', '#94a3b8', 12),
    ],
    // Project lifecycle phases per Portuguese RJUE / LNEC practice
    // including site supervision. Maps cleanly to BCF <Stage>.
    stages: [
      make('Programa Base', '#fbbf24', 0),
      make('Estudo Prévio', '#fb923c', 1),
      make('Anteprojecto', '#fb7185', 2),
      make('Projecto Base', '#a78bfa', 3),
      make('Projecto de Execução', '#60a5fa', 4),
      make('Acompanhamento de Obra', '#4ade80', 5),
    ],
  }
}

/** Side-output of parseBcfZip: the snapshot PNG/JPG bytes referenced
 * by each topic's `<Viewpoints>` blocks. The slice consumes these at
 * import time and inserts them into `viewpointsStore` so attached
 * images survive a round-trip and are visible immediately on the
 * topic's "Viewpoints anexados" section. The bytes are intentionally
 * kept out of `ParsedBcf` so the IDB persistence of `LoadedBcf` does
 * not balloon. */
export interface ImportedViewpoint {
  /** Stable id reused on round-trip. Mirrors the BCF Viewpoints Guid
   * when present; falls back to a fresh UUID. */
  id: string
  topicGuid: string
  filename: string
  snapshotBytes: Uint8Array | null
  /** Parsed `<VisualizationInfo>` xml (BCF 2.1), left as raw bytes
   * for now. Onda 5+ will parse the camera so applyLocalViewpoint
   * has something to drive the viewer with. */
  bcfvBytes: Uint8Array | null
}

export interface ParseResult {
  parsed: ParsedBcf
  importedViewpoints: ImportedViewpoint[]
}

const TOPIC_FOLDER_RE = /^([0-9a-fA-F-]{36})\/markup\.bcf$/

async function unzipAsync(buf: Uint8Array): Promise<Record<string, Uint8Array>> {
  return new Promise((resolve, reject) => {
    unzip(buf, (err, files) => {
      if (err) reject(err)
      else resolve(files)
    })
  })
}

function textOf(parent: Element | null, tag: string): string | null {
  if (!parent) return null
  const el = parent.getElementsByTagName(tag)[0]
  if (!el) return null
  const t = el.textContent
  return t === null || t === '' ? null : t
}

/** Como `textOf`, mas só entre os filhos directos. Na 3.0 os comentários
 * e os viewpoints vivem dentro do `<Topic>` e têm `<Index>`,
 * `<ModifiedDate>` e `<ModifiedAuthor>` seus; uma procura em
 * profundidade apanhava-os como se fossem do tópico. */
function textOfChild(parent: Element, tag: string): string | null {
  for (const el of Array.from(parent.children)) {
    if (el.tagName !== tag) continue
    const t = el.textContent
    return t === null || t === '' ? null : t
  }
  return null
}

function detectVersion(files: Record<string, Uint8Array>): BcfVersion {
  const versionFile = files['bcf.version']
  if (!versionFile) return 'unknown'
  const xml = strFromU8(versionFile)
  const doc = new DOMParser().parseFromString(xml, 'application/xml')
  const root = doc.documentElement
  if (!root) return 'unknown'
  const ver = root.getAttribute('VersionId') ?? textOf(root, 'VersionId')
  if (ver === '3.0') return '3.0'
  if (ver === '2.1') return '2.1'
  if (ver === '2.0') return '2.0'
  return 'unknown'
}

interface ViewpointRef {
  guid: string
  bcfvFile: string | null
  snapshotFile: string | null
}

/** Na 2.1 cada referência é um `<Viewpoints>` filho do `<Markup>`; na
 * 3.0 é um `<ViewPoint>` dentro de `<Topic><Viewpoints>`. Lêem-se as
 * duas formas, venha o ficheiro de onde vier. */
function parseViewpointRefs(markupEl: Element, topicEl: Element): ViewpointRef[] {
  const out: ViewpointRef[] = []
  const candidatos: Element[] = []
  for (const vp of Array.from(markupEl.getElementsByTagName('Viewpoints'))) {
    if (vp.parentElement === markupEl) candidatos.push(vp)
  }
  for (const vp of Array.from(topicEl.getElementsByTagName('ViewPoint'))) {
    const contentor = vp.parentElement
    if (contentor?.tagName === 'Viewpoints' && contentor.parentElement === topicEl) {
      candidatos.push(vp)
    }
  }
  for (const vp of candidatos) {
    out.push({
      guid: vp.getAttribute('Guid') ?? crypto.randomUUID(),
      bcfvFile: textOf(vp, 'Viewpoint'),
      snapshotFile: textOf(vp, 'Snapshot'),
    })
  }
  return out
}

function parseMarkup(
  xml: string,
  fallbackGuid: string,
): { topic: BcfTopic; vpRefs: ViewpointRef[] } | null {
  const doc = new DOMParser().parseFromString(xml, 'application/xml')
  const parserError = doc.getElementsByTagName('parsererror')[0]
  if (parserError) return null
  const topicEl =
    doc.getElementsByTagName('Topic')[0] ?? doc.documentElement
  if (!topicEl) return null

  const guid = topicEl.getAttribute('Guid') ?? fallbackGuid
  const type =
    topicEl.getAttribute('TopicType') ?? textOfChild(topicEl, 'TopicType')
  const status =
    topicEl.getAttribute('TopicStatus') ?? textOfChild(topicEl, 'TopicStatus')
  const indexStr = textOfChild(topicEl, 'Index')
  const index = indexStr !== null ? Number.parseInt(indexStr, 10) : null

  // Labels shape differs across spec versions:
  // - BCF 2.x: each label is one `<Labels>foo</Labels>` element with
  //   text content; multiple `<Labels>` siblings under <Topic>.
  // - BCF 3.0: a single `<Labels>` container with `<Label>foo</Label>`
  //   children.
  // We iterate every `<Labels>` that is a direct child of <Topic>:
  // when it has element children we treat them as the BCF 3.0 list,
  // otherwise we read its text content as a single BCF 2.x label.
  const labels: string[] = []
  for (const labelsEl of Array.from(topicEl.getElementsByTagName('Labels'))) {
    if (labelsEl.parentElement !== topicEl) continue
    const childLabels = Array.from(labelsEl.getElementsByTagName('Label'))
    if (childLabels.length > 0) {
      for (const child of childLabels) {
        const t = child.textContent?.trim()
        if (t) labels.push(t)
      }
    } else {
      const t = labelsEl.textContent?.trim()
      if (t) labels.push(t)
    }
  }

  const comments: BcfComment[] = []
  const markupEl = doc.documentElement
  for (const commentEl of Array.from(markupEl.getElementsByTagName('Comment'))) {
    // 2.1: <Markup><Comment>. 3.0: <Markup><Topic><Comments><Comment>.
    // O <Comment> de texto dentro de cada comentário fica de fora pelos
    // dois testes.
    const pai = commentEl.parentElement
    const eDa21 = pai === markupEl
    const eDa30 = pai?.tagName === 'Comments' && pai.parentElement === topicEl
    if (!eDa21 && !eDa30) continue
    comments.push({
      guid: commentEl.getAttribute('Guid') ?? crypto.randomUUID(),
      date: textOf(commentEl, 'Date'),
      author: textOf(commentEl, 'Author'),
      comment: textOf(commentEl, 'Comment'),
      modifiedDate: textOf(commentEl, 'ModifiedDate'),
      modifiedAuthor: textOf(commentEl, 'ModifiedAuthor'),
      status: textOf(commentEl, 'Status'),
    })
  }
  comments.sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''))

  const vpRefs = parseViewpointRefs(markupEl, topicEl)
  const relatedTopics = parseRelatedTopics(topicEl, guid)

  const topic: BcfTopic = {
    guid,
    index,
    title: textOfChild(topicEl, 'Title') ?? '(sem título)',
    type,
    status,
    priority: textOfChild(topicEl, 'Priority'),
    assignedTo: textOfChild(topicEl, 'AssignedTo'),
    description: textOfChild(topicEl, 'Description'),
    creationAuthor: textOfChild(topicEl, 'CreationAuthor'),
    creationDate: textOfChild(topicEl, 'CreationDate'),
    modifiedAuthor: textOfChild(topicEl, 'ModifiedAuthor'),
    modifiedDate: textOfChild(topicEl, 'ModifiedDate'),
    dueDate: textOfChild(topicEl, 'DueDate'),
    stage: textOfChild(topicEl, 'Stage'),
    labels,
    comments,
    viewpointCount: vpRefs.length,
    viewpointRefs: vpRefs.map((r) => r.guid),
    // Watchers come from the sidecar vn2r-topics.json, applied later
    // by the parser entrypoint, kept empty here.
    watchers: [],
    relatedTopics,
  }
  return { topic, vpRefs }
}

/** Os tópicos relacionados, nas duas formas: 2.1 `<RelatedTopic>` filho
 * directo do `<Topic>`; 3.0 `<RelatedTopic>` dentro de `<RelatedTopics>`.
 * Lêem-se as duas venha o ficheiro de onde vier. Sem repetidos, pela
 * ordem do ficheiro, e sem o próprio tópico. */
function parseRelatedTopics(topicEl: Element, proprio: string): string[] {
  const out: string[] = []
  const juntar = (el: Element) => {
    const g = el.getAttribute('Guid')?.trim()
    if (g && g !== proprio && !out.includes(g)) out.push(g)
  }
  for (const el of Array.from(topicEl.children)) {
    if (el.tagName === 'RelatedTopic') juntar(el)
    else if (el.tagName === 'RelatedTopics') {
      for (const filho of Array.from(el.children)) {
        if (filho.tagName === 'RelatedTopic') juntar(filho)
      }
    }
  }
  return out
}

/** Read children of an Extensions container (e.g. <TopicStatuses>)
 * and return them as plain strings, in declaration order. Tolerates
 * BCF 2.x (children are <TopicStatus>Foo</TopicStatus>) and BCF 3.0
 * (same shape; differences are at the schema location, not the
 * element form). */
function readCatalogChildren(parent: Element, childTag: string): string[] {
  const out: string[] = []
  for (const el of Array.from(parent.getElementsByTagName(childTag))) {
    if (el.parentElement !== parent) continue
    const t = el.textContent?.trim()
    if (t) out.push(t)
  }
  return out
}

/** Map plain strings from extensions.xml into BcfCatalogEntry[]. The
 * sidecar `vn2r-extensions.json` (read separately) overrides colour
 * and sort order; this fallback path runs when the sidecar isn't
 * present, so every imported `.bcfzip` lands with a catalog the user
 * can edit. */
function entriesFromStrings(
  values: string[],
  defaultColor: (idx: number) => string,
): BcfCatalogEntry[] {
  return values.map((v, i) => ({
    id: crypto.randomUUID(),
    label: v,
    color: defaultColor(i),
    sortOrder: i,
    source: 'xml',
  }))
}

const FALLBACK_PALETTE = [
  '#60a5fa', '#fb7185', '#fbbf24', '#34d399', '#a78bfa',
  '#f472b6', '#facc15', '#4ade80', '#fb923c', '#94a3b8',
]
const palette = (i: number) => FALLBACK_PALETTE[i % FALLBACK_PALETTE.length]

function parseExtensionsXml(xml: string): BcfExtensions {
  const fallback = defaultExtensions()
  const doc = new DOMParser().parseFromString(xml, 'application/xml')
  if (doc.getElementsByTagName('parsererror')[0]) return fallback
  const root = doc.documentElement
  if (!root) return fallback

  const find = (containerTag: string, childTag: string): string[] => {
    const c = root.getElementsByTagName(containerTag)[0]
    return c ? readCatalogChildren(c, childTag) : []
  }

  const statusValues = find('TopicStatuses', 'TopicStatus')
  const priorityValues = find('Priorities', 'Priority')
  const typeValues = find('TopicTypes', 'TopicType')
  const labelValues = find('TopicLabels', 'TopicLabel')
  const stageValues = find('Stages', 'Stage')

  // Heuristic: any status containing "clos" / "resol" / "done" is
  // terminal. The sidecar JSON overrides this; this is the cold-start
  // best guess for a .bcfzip without VN2R metadata.
  const isTerminal = (s: string) =>
    /clos|resolv|done|fechad/i.test(s)

  return {
    statuses:
      statusValues.length > 0
        ? statusValues.map((v, i) => ({
            id: crypto.randomUUID(),
            label: v,
            color: palette(i),
            sortOrder: i,
            isTerminal: isTerminal(v),
            source: 'xml',
          }))
        : fallback.statuses,
    priorities:
      priorityValues.length > 0
        ? entriesFromStrings(priorityValues, palette)
        : fallback.priorities,
    types:
      typeValues.length > 0
        ? entriesFromStrings(typeValues, palette)
        : fallback.types,
    labels:
      labelValues.length > 0
        ? entriesFromStrings(labelValues, palette)
        : fallback.labels,
    stages:
      stageValues.length > 0
        ? entriesFromStrings(stageValues, palette)
        : fallback.stages,
  }
}

/** Apply the sidecar `vn2r-extensions.json` over the catalogs that
 * came from `extensions.xml`. Matches by `label` (case-insensitive)
 * to merge colour / sortOrder / isTerminal; labels not in the
 * sidecar keep their xml-derived values. The sidecar shape mirrors
 * `BcfExtensions`. */
function applyExtensionsSidecar(
  base: BcfExtensions,
  raw: unknown,
): BcfExtensions {
  if (!raw || typeof raw !== 'object') return base
  const r = raw as Record<string, unknown>
  const next: BcfExtensions = {
    statuses: [...base.statuses],
    priorities: [...base.priorities],
    types: [...base.types],
    labels: [...base.labels],
    stages: [...base.stages],
  }

  type Entry = { label?: unknown; color?: unknown; sortOrder?: unknown; isTerminal?: unknown; id?: unknown }

  const mergeOne = (
    cur: BcfCatalogEntry,
    side: Entry,
  ): BcfCatalogEntry => ({
    ...cur,
    color: typeof side.color === 'string' ? side.color : cur.color,
    sortOrder:
      typeof side.sortOrder === 'number' ? side.sortOrder : cur.sortOrder,
  })

  const mergeArr = <T extends BcfCatalogEntry>(
    arr: T[],
    side: unknown,
    extraMerge?: (cur: T, side: Entry) => T,
  ): T[] => {
    if (!Array.isArray(side)) return arr
    const byLabel = new Map<string, Entry>()
    for (const s of side) {
      if (s && typeof s === 'object') {
        const e = s as Entry
        if (typeof e.label === 'string') byLabel.set(e.label.toLowerCase(), e)
      }
    }
    return arr.map((cur) => {
      const e = byLabel.get(cur.label.toLowerCase())
      if (!e) return cur
      const merged = mergeOne(cur, e) as T
      return extraMerge ? extraMerge(merged, e) : merged
    })
  }

  next.statuses = mergeArr(next.statuses, r.statuses, (cur, e) => ({
    ...cur,
    isTerminal:
      typeof e.isTerminal === 'boolean' ? e.isTerminal : cur.isTerminal,
  }))
  next.priorities = mergeArr(next.priorities, r.priorities)
  next.types = mergeArr(next.types, r.types)
  next.labels = mergeArr(next.labels, r.labels)
  next.stages = mergeArr(next.stages, r.stages)
  return next
}

/** Add to each catalog any value that appears on a topic but isn't
 * already in the catalog (case-insensitive match). Useful when the
 * imported `.bcfzip` has no `extensions.xml` (or has incomplete
 * lists): the user shouldn't have to manually re-add every label
 * already in use. New entries get a fallback colour and sort to the
 * end of the list. */
function seedFromTopics(
  base: BcfExtensions,
  topics: BcfTopic[],
): BcfExtensions {
  const collect = <T extends BcfCatalogEntry>(
    entries: T[],
    values: string[],
    factory: (label: string, sortOrder: number) => T,
  ): T[] => {
    const known = new Set(entries.map((e) => e.label.toLowerCase()))
    const seen = new Set<string>()
    const additions: T[] = []
    for (const v of values) {
      if (!v) continue
      const lower = v.toLowerCase()
      if (known.has(lower) || seen.has(lower)) continue
      seen.add(lower)
      additions.push(factory(v, entries.length + additions.length))
    }
    return additions.length === 0 ? entries : [...entries, ...additions]
  }
  const colorAt = (i: number) => palette(i)
  return {
    statuses: collect(
      base.statuses,
      topics.map((t) => t.status ?? '').filter(Boolean),
      (label, sortOrder) => ({
        id: crypto.randomUUID(),
        label,
        color: colorAt(sortOrder),
        sortOrder,
        // Heuristic: if label looks terminal, mark it. Same regex as
        // parseExtensionsXml. User can flip in the catalogs panel.
        isTerminal: /clos|resolv|done|fechad/i.test(label),
        source: 'topic',
      }),
    ),
    priorities: collect(
      base.priorities,
      topics.map((t) => t.priority ?? '').filter(Boolean),
      (label, sortOrder) => ({
        id: crypto.randomUUID(),
        label,
        color: colorAt(sortOrder),
        sortOrder,
        source: 'topic',
      }),
    ),
    types: collect(
      base.types,
      topics.map((t) => t.type ?? '').filter(Boolean),
      (label, sortOrder) => ({
        id: crypto.randomUUID(),
        label,
        color: colorAt(sortOrder),
        sortOrder,
        source: 'topic',
      }),
    ),
    labels: collect(
      base.labels,
      topics.flatMap((t) => t.labels),
      (label, sortOrder) => ({
        id: crypto.randomUUID(),
        label,
        color: colorAt(sortOrder),
        sortOrder,
        source: 'topic',
      }),
    ),
    stages: collect(
      base.stages,
      topics.map((t) => t.stage ?? '').filter(Boolean),
      (label, sortOrder) => ({
        id: crypto.randomUUID(),
        label,
        color: colorAt(sortOrder),
        sortOrder,
        source: 'topic',
      }),
    ),
  }
}

/** Parse the sidecar `vn2r-topics.json` (watchers per topic guid) and
 * return a guid->watchers map. Tolerates malformed JSON silently. */
function parseTopicsSidecar(raw: Uint8Array): Map<string, string[]> {
  const out = new Map<string, string[]>()
  try {
    const text = strFromU8(raw)
    const obj = JSON.parse(text) as unknown
    if (!obj || typeof obj !== 'object') return out
    const topics = (obj as { topics?: unknown }).topics
    if (!Array.isArray(topics)) return out
    for (const t of topics) {
      if (!t || typeof t !== 'object') continue
      const e = t as { guid?: unknown; watchers?: unknown }
      if (typeof e.guid !== 'string') continue
      if (!Array.isArray(e.watchers)) continue
      out.set(
        e.guid,
        e.watchers.filter((w): w is string => typeof w === 'string'),
      )
    }
  } catch {
    /* ignore */
  }
  return out
}

function parseProjectInfo(
  files: Record<string, Uint8Array>,
): { id: string | null; name: string | null } {
  const file = files['project.bcfp']
  if (!file) return { id: null, name: null }
  try {
    const xml = strFromU8(file)
    const doc = new DOMParser().parseFromString(xml, 'application/xml')
    const projectEl =
      doc.getElementsByTagName('Project')[0]
      ?? doc.getElementsByTagName('ProjectInfo')[0]
    if (!projectEl) return { id: null, name: null }
    const id =
      projectEl.getAttribute('ProjectId')
      ?? projectEl.getAttribute('Guid')
      ?? textOf(projectEl, 'ProjectId')
      ?? textOf(projectEl, 'Guid')
    const name = textOf(projectEl, 'Name')
    return { id: id || null, name: name || null }
  } catch {
    return { id: null, name: null }
  }
}

export async function parseBcfZip(buf: ArrayBuffer): Promise<ParseResult> {
  const files = await unzipAsync(new Uint8Array(buf))
  const version = detectVersion(files)
  const project = parseProjectInfo(files)
  const topics: BcfTopic[] = []
  const importedViewpoints: ImportedViewpoint[] = []
  const warnings: string[] = []

  // Catalogs come from extensions.xml (standard) overlaid with
  // vn2r-extensions.json (our colour / sort metadata).
  let extensions: BcfExtensions
  if (files['extensions.xml']) {
    extensions = parseExtensionsXml(strFromU8(files['extensions.xml']))
  } else {
    extensions = defaultExtensions()
  }
  if (files['vn2r-extensions.json']) {
    try {
      const sideRaw = JSON.parse(strFromU8(files['vn2r-extensions.json']))
      extensions = applyExtensionsSidecar(extensions, sideRaw)
    } catch {
      /* ignore, keep xml-derived catalogs */
    }
  }

  // Watchers per topic come from the sidecar, applied after we
  // parse each topic so the merge is by guid, not by ordering.
  const watchersByGuid = files['vn2r-topics.json']
    ? parseTopicsSidecar(files['vn2r-topics.json'])
    : new Map<string, string[]>()

  for (const path of Object.keys(files)) {
    const m = TOPIC_FOLDER_RE.exec(path)
    if (!m) continue
    const topicGuid = m[1]
    const xml = strFromU8(files[path])
    const parsedMarkup = parseMarkup(xml, topicGuid)
    if (parsedMarkup === null) {
      warnings.push(`Falha a interpretar markup do tópico ${topicGuid}.`)
      continue
    }
    const w = watchersByGuid.get(parsedMarkup.topic.guid)
    if (w) parsedMarkup.topic.watchers = w
    topics.push(parsedMarkup.topic)

    // Pull each declared viewpoint's snapshot + bcfv from the zip so
    // they survive into the local viewpointsStore.
    for (const ref of parsedMarkup.vpRefs) {
      const snapPath = ref.snapshotFile
        ? `${topicGuid}/${ref.snapshotFile}`
        : null
      const bcfvPath = ref.bcfvFile
        ? `${topicGuid}/${ref.bcfvFile}`
        : null
      importedViewpoints.push({
        id: ref.guid,
        topicGuid,
        filename: ref.snapshotFile ?? ref.bcfvFile ?? `${ref.guid}.bcfv`,
        snapshotBytes: snapPath ? files[snapPath] ?? null : null,
        bcfvBytes: bcfvPath ? files[bcfvPath] ?? null : null,
      })
    }
  }

  topics.sort((a, b) => {
    if (a.index !== null && b.index !== null) return a.index - b.index
    return (a.creationDate ?? '').localeCompare(b.creationDate ?? '')
  })

  // Auto-seed catalogs with values found on topics but absent from
  // extensions.xml / sidecar / defaults. This runs LAST so explicit
  // sidecar metadata (colour, sort) wins for known labels and only
  // unknown values get appended.
  extensions = seedFromTopics(extensions, topics)

  return {
    parsed: {
      version,
      sourceProjectId: project.id,
      sourceProjectName: project.name,
      topics,
      extensions,
      warnings,
    },
    importedViewpoints,
  }
}
